<template>
    <div>
        <!-- Loading -->
        <div v-if="!data && status === 'pending'" class="flex justify-center items-center min-h-[400px]">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>

        <!-- Content -->
        <template v-else-if="data">
            <ProjectView :project="data" />

            <RecommendedItems v-if="isProd" :id="data.id" :collection="collection">
                {{ recommendedListHeading }}
            </RecommendedItems>
        </template>

        <!-- Error state -->
        <div v-else class="text-center py-20">
            <p class="text-gray-600 dark:text-txt-body">Projekt sa nepodarilo načítať</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { EntityType } from '#shared/types/EntityType'
import type { Project } from '#shared/types/Project'
import { VisibilityType } from '#shared/types/visibility'
import { OverviewType } from '#shared/types/Overview'
import { isPublicVolunteeringWeekDraftProject } from '#shared/utils/publicProjectFilters'
import ProjectCompletedModal from '@/components/common/modals/ProjectCompletedModal.vue'
import { useJsonLd } from '@/composables/head/useJsonLd'
import { useSharing } from '@/composables/sharing/useSharing'
import { isProduction } from '@/utils/helpers'
import { formatSlovakRegionLabel } from '@/utils/slovakRegion'
import {
    absoluteUrl,
    buildBreadcrumbList,
    buildJobLocations,
    capitalizeFirst,
    collapseWhitespace,
    composeDescription,
    escapeHtml,
    htmlToPlainText,
    REMOTE_JOB_IN_SLOVAKIA,
    SECTION_SOCIAL_IMAGES,
    toIsoDateTime,
    withSiteName
} from '@/utils/structuredData'
import { useModal } from 'vue-final-modal'

const route = useRoute()
const router = useRouter()
const store = useApplicationStore()
const { t: $t } = useI18n()
const { open: openProjectCompletedModal, close: closeProjectCompletedModal, patchOptions: patchProjectCompletedModal } = useModal({
    component: ProjectCompletedModal
})

// Composables
const { getSharingUrl } = useSharing()

// Data
const isProd = isProduction()
const projectId = route.params.id as string
const PROJECT_DETAIL_CACHE_CONTROL = 'no-store, max-age=0, must-revalidate'
const PUBLIC_PROJECT_DETAIL_VISIBILITIES = new Set<VisibilityType>([
    VisibilityType.published,
    VisibilityType.unlisted
])

if (import.meta.server) {
    useResponseHeader('Cache-Control').value = PROJECT_DETAIL_CACHE_CONTROL
}

// Fetch data using server API (works on both SSR and client)
const { data, error, status } = await useFetch<Project>(`/api/projects/${projectId}`, {
    key: `project-detail-${projectId}`
})

function navigateToUnavailableProject() {
    return navigateTo({ path: '/ponuka-nie-je-aktualne-dostupna',
        query: {
            type: OverviewType.VOLUNTEERING,
            id: projectId
        }
    })
}

// Handle private/draft/unavailable visibility before rendering project details.
if (
    data.value &&
    !PUBLIC_PROJECT_DETAIL_VISIBILITIES.has(data.value.visibility as VisibilityType) &&
    !isPublicVolunteeringWeekDraftProject(data.value)
) {
    await navigateToUnavailableProject()
}

// Handle error
if (error.value) {
    if (error.value.statusCode === 404) {
        await navigateToUnavailableProject()
    } else {
        throw createError({
            statusCode: error.value.statusCode || 500,
            message: error.value.message || 'Project not found'
        })
    }
}

// Computed properties
const isProjectOwner = computed(() => {
    return store.userId === data.value?.createdById
})

const projectTitle = computed(() => {
    return capitalizeFirst(collapseWhitespace(data.value?.title))
})

const projectOrgName = computed(() => collapseWhitespace(data.value?.orgMeta?.title))
const projectRegion = computed(() => formatSlovakRegionLabel(data.value?.geoRegion))
const isRemoteProject = computed(() => !!data.value?.how?.includes('remotely'))
const isInPersonProject = computed(() => !!data.value?.how?.includes('in_person'))

const projectPageTitle = computed(() => withSiteName(`${projectTitle.value} – dobrovoľníctvo`))

/** Places from geoCity, which is free text: one town, a comma-separated list or a street address. */
const projectPlaces = computed(() => {
    if (!isInPersonProject.value) return []
    const places = buildJobLocations(data.value?.geoCity?.split(','), { region: projectRegion.value })
    if (places.length) return places
    // "celé Slovensko" and the like: somewhere in Slovakia. Without any geoCity, the region.
    if (collapseWhitespace(data.value?.geoCity) || projectRegion.value) {
        const region = collapseWhitespace(data.value?.geoCity) ? undefined : projectRegion.value
        return [{ '@type': 'Place', address: { '@type': 'PostalAddress', addressRegion: region, addressCountry: 'SK' } }]
    }
    return []
})

const projectTowns = computed(() => {
    return projectPlaces.value
        .map((place) => (place.address as { addressLocality?: string }).addressLocality)
        .filter(Boolean)
})

const projectDescription = computed(() => {
    const towns = projectTowns.value
    const place = towns.length
        ? `${towns.slice(0, 3).join(', ')}${towns.length > 3 ? ' a ďalšie' : ''}`
        : isInPersonProject.value && !collapseWhitespace(data.value?.geoCity)
          ? projectRegion.value
          : ''
    const remote = isRemoteProject.value ? (place ? 'aj na diaľku' : 'na diaľku') : ''
    const where = [place, remote].filter(Boolean).join(', ')
    const lead = projectOrgName.value
        ? `Dobrovoľnícka príležitosť v organizácii ${projectOrgName.value}${where ? ` (${where})` : ''}.`
        : `Dobrovoľnícka príležitosť${where ? ` (${where})` : ''}.`
    return composeDescription([lead, htmlToPlainText(data.value?.about || data.value?.desc || data.value?.benefits)])
})

const projectImage = computed(() => {
    const image = data.value?.image || data.value?.orgMeta?.image
    return image ? getSharingUrl('projects', image) : SECTION_SOCIAL_IMAGES.volunteeringProjects
})

const canonicalUrl = computed(() => {
    return absoluteUrl(`/dobrovolnictvo/dobrovolnicke-projekty/${data.value?.slug || projectId}`)
})

const projectUrl = computed(() => {
    return `${window.location.origin}/dobrovolnictvo/dobrovolnicke-projekty/${projectId}`
})

const collection = computed(() => {
    return isProjectOwner.value ? 'volunteers' : 'projects'
})

const recommendedListHeading = computed(() => {
    const headlineType = isProjectOwner.value ? EntityType.Volunteer : EntityType.Project
    return $t(`relatedItems.${headlineType}.headline`, 'sk') as string
})

/** Rich-text sections as ProjectViewLayoutLeft shows them, for the JobPosting description. */
const PROJECT_CONTENT_SECTIONS: Array<{ key: 'about' | 'impact' | 'progress' | 'benefits'; heading: string }> = [
    { key: 'impact', heading: 'Prečo túto aktivitu robíme?' },
    { key: 'about', heading: 'S čím potrebujeme pomôcť?' },
    { key: 'benefits', heading: 'Čo dobrovoľníci/čky získajú?' },
    { key: 'progress', heading: 'S akou podporou môžu dobrovoľníci počítať?' }
]

// Unlisted projects (test or template projects, shared by link only) open but stay out of search.
// Týždeň dobrovoľníctva drafts are listed publicly during the campaign, so they count as published.
const isIndexableProject = computed(() => {
    const project = data.value
    if (!project) return false
    return project.visibility === VisibilityType.published || isPublicVolunteeringWeekDraftProject(project)
})

/**
 * Google supports volunteer positions as JobPosting with employmentType VOLUNTEER. Emitted
 * only when the required fields exist: an organization and a place (or remote work).
 */
const volunteerPostingJsonLd = computed(() => {
    const project = data.value
    if (!project || !isIndexableProject.value || !project.orgId || !projectOrgName.value) return null

    const places = projectPlaces.value
    if (!places.length && !isRemoteProject.value) return null

    const description = PROJECT_CONTENT_SECTIONS.filter((section) => htmlToPlainText(project[section.key]))
        .map((section) => `<p><strong>${section.heading}</strong></p>${project[section.key]}`)
        .join('')

    return {
        '@type': 'JobPosting',
        title: projectTitle.value,
        description: description || `<p>${escapeHtml(projectDescription.value)}</p>`,
        datePosted: toIsoDateTime(project.publishedAt) || toIsoDateTime(project.createdAt),
        // Dated actions (Týždeň dobrovoľníctva) end with the action, the rest when they get unpublished.
        validThrough: toIsoDateTime(project.endDateAt) || toIsoDateTime(project.unpublishAt),
        employmentType: 'VOLUNTEER',
        hiringOrganization: {
            '@type': 'Organization',
            name: projectOrgName.value,
            logo: project.orgMeta?.image
        },
        jobLocation: places,
        ...(isRemoteProject.value ? REMOTE_JOB_IN_SLOVAKIA : {}),
        identifier: { '@type': 'PropertyValue', name: projectOrgName.value, value: project.id },
        url: canonicalUrl.value
    }
})

useJsonLd(() => [
    volunteerPostingJsonLd.value,
    isIndexableProject.value &&
        buildBreadcrumbList([
            { name: 'Domov', path: '/' },
            { name: 'Dobrovoľníctvo', path: '/dobrovolnictvo' },
            { name: 'Dobrovoľnícke projekty', path: '/dobrovolnictvo/dobrovolnicke-projekty' },
            { name: projectTitle.value, path: canonicalUrl.value }
        ])
])

// Meta config for SEO and GTM
const headConfig = computed(() => ({
    title: projectPageTitle.value,
    link: [
        // `key` dedupes against the site-wide canonical set in app.vue
        { rel: 'canonical' as const, key: 'canonical', href: canonicalUrl.value }
    ],
    meta: [
        ...(data.value && !isIndexableProject.value ? [{ name: 'robots', content: 'noindex, follow' }] : []),
        { name: 'description', content: projectDescription.value },
        { property: 'og:title', content: projectPageTitle.value },
        { property: 'og:description', content: projectDescription.value },
        { property: 'og:image', content: projectImage.value },
        { property: 'og:image:alt', content: `Obrázok dobrovoľníckeho projektu ${projectTitle.value}` },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: canonicalUrl.value }
    ],
    script: [
        {
            key: 'gtm-project-page',
            innerHTML: `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'unique_id': '${data.value?.id || ''}'
        });
      `,
            type: 'text/javascript' as const
        }
    ]
}))

useHead(headConfig)

// Set dynamic breadcrumb title when project data is available
onMounted(() => {
    if (process.client && projectTitle.value) {
        nextTick(() => {
            const { $dynamicTitle } = useNuxtApp()
            if ($dynamicTitle) {
                $dynamicTitle(projectTitle.value)
            }
        })
    }

    if (process.client && route.query.created === 'true' && data.value) {
        nextTick(openCreatedProjectModal)
    }
})

async function openCreatedProjectModal() {
    if (!data.value) return

    patchProjectCompletedModal({
        attrs: {
            id: data.value.id,
            title: data.value.title || '',
            url: projectUrl.value,
            onClose: (result?: any) => {
                closeProjectCompletedModal()
                return result
            }
        }
    })

    await openProjectCompletedModal()

    await router.replace({
        name: 'dobrovolnictvo-dobrovolnicke-projekty-id',
        params: { id: projectId },
        query: {}
    })
}
</script>
